import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  PREFERENCE_SCHEMAS,
  type PreferenceKey,
  type PreferenceListType,
  type PreferenceValue,
} from '@aeris/contracts'

import { fetchPreferences, savePreference } from './api/preferences.ts'

/**
 * Uma preferência de quem está logado, com o padrão enquanto não há nada
 * gravado — F127.
 *
 * ## Por que a tela muda antes de o servidor responder
 *
 * Marcar uma coluna e esperar a rede para ela aparecer é uma lista que parece
 * travada. A mudança vai para o cache na hora; se o servidor recusar, o cache
 * volta a ser o que o servidor tem.
 *
 * ## Por que as gravações andam em fila
 *
 * Cada gravação carrega a `version` lida, e o servidor recusa versão velha.
 * Duas colunas marcadas em sequência rápida sairiam com a MESMA versão e a
 * segunda voltaria 409. O `scope` põe as gravações da mesma chave em fila, e
 * cada uma lê a versão do cache no momento em que sai — já atualizada pela
 * anterior.
 */

const QUERY_KEY = ['preferences'] as const

export type UsePreference<K extends PreferenceKey> = {
  readonly value: PreferenceValue<K>
  /** Já carregou do servidor (gravado ou não). Antes disso vale o padrão. */
  readonly isLoaded: boolean
  readonly save: (next: PreferenceValue<K>) => void
  readonly error: boolean
}

export const usePreference = <K extends PreferenceKey>(
  key: K,
  fallback: PreferenceValue<K>
): UsePreference<K> => {
  const client = useQueryClient()

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async ({ signal }) => await fetchPreferences(signal),
    // Muda por esta tela, e é ela que atualiza o cache.
    staleTime: Number.POSITIVE_INFINITY,
  })

  const entry = query.data?.preferences.find((item) => item.key === key)
  const parsed =
    entry === undefined ? null : PREFERENCE_SCHEMAS[key].safeParse(entry.value)
  const value = (
    parsed?.success === true ? parsed.data : fallback
  ) as PreferenceValue<K>

  const mutation = useMutation({
    scope: { id: `preference:${key}` },
    mutationFn: async (next: PreferenceValue<K>) => {
      const latest = client
        .getQueryData<PreferenceListType>(QUERY_KEY)
        ?.preferences.find((item) => item.key === key)
      return await savePreference(key, {
        value: next,
        version: latest?.version ?? 0,
      })
    },
    onSuccess: (saved) => {
      client.setQueryData<PreferenceListType>(QUERY_KEY, (current) => ({
        preferences: [
          ...(current?.preferences ?? []).filter((item) => item.key !== key),
          saved,
        ],
      }))
    },
    onError: async () => {
      await client.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })

  return {
    value: mutation.isPending ? mutation.variables : value,
    isLoaded: query.isSuccess,
    save: (next) => {
      mutation.mutate(next)
    },
    error: query.isError || mutation.isError,
  }
}
